import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { ChatMessage } from "../utils/messageHelpers";
import { shouldAutoSendAfterApprovals } from "../utils/approvalHelpers";
import { adaptMessagesForBackend } from "../utils/messageAdapter";
import { sanitizeStreamResponse } from "../utils/streamChunkSanitizer";

const API_BASE_URL =
  (process.env.REACT_APP_API_URL || "https://api.autumnplatform.com").replace(/\/$/, "");

/**
 * Parse Type Validation errors from AI SDK v6 into user-friendly messages.
 * Bias toward network/connectivity issues rather than technical errors.
 *
 * @param error - The error object from AI SDK
 * @returns User-friendly error message (never shows raw JSON or validation details)
 */
function parseTypeValidationError(error: Error): string {
  const errorMessage = error.message || '';

  // Try multiple JSON extraction patterns to handle various error formats
  try {
    const patterns = [
      /\[{[\s\S]*}\]/,           // Array of objects: [{...}]
      /\{[\s\S]*"code"[\s\S]*\}/, // Single object with "code": {...}
      /\n(\[{[\s\S]*}\])$/,       // JSON at end after newline
    ];

    for (const pattern of patterns) {
      const jsonMatch = errorMessage.match(pattern);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);

        if (Array.isArray(parsed) && parsed.length > 0) {
          // Log for debugging but don't expose to user
          console.group('🔍 [Error Parser] Validation errors detected:');
          console.log('Error count:', parsed.length);
          console.log('Error types:', parsed.map((e: any) => e.code).join(', '));
          console.groupEnd();
        } else if (parsed.code) {
          // Single validation error object
          console.log('🔍 [Error Parser] Single validation error:', parsed.code);
        }

        // ALL validation errors get the same user-friendly message
        // Bias toward "connectivity" rather than "bad data"
        return "We're having trouble connecting to our AI service. Please try again.";
      }
    }
  } catch (parseError) {
    console.warn('[Error Parser] Could not parse validation error:', parseError);
  }

  // Fallback for any Type Validation error
  return "Connection issue with our AI service. Please try again.";
}

export interface UseChatMessagesOptions {
  /**
   * Optional conversation ID to use for this chat session.
   * If not provided, a new conversation ID will be generated.
   */
  conversationId?: string;
}

/**
 * Shared AI SDK v6 useChat hook configuration
 *
 * Provides consistent streaming, tool approval, and message management
 * across both Production and Debug UIs.
 *
 * Migrated to /ai/chat-v2 with message transformation via prepareSendMessagesRequest.
 *
 * @param options - Configuration options
 * @returns AI SDK useChat hook result with messages, sendMessage, status, etc.
 */
export function useChatMessages(options?: UseChatMessagesOptions) {
  // Use provided conversationId or generate a new one
  const chatId = options?.conversationId || crypto.randomUUID();

  // Chat v2 endpoint (custom orchestrator)
  const chatEndpoint = `${API_BASE_URL}/ai/chat`;

  // AI SDK v6 useChat hook with approval flow auto-continue
  const chatHook = useChat({
    id: chatId,
    transport: new DefaultChatTransport({
      api: chatEndpoint,
      headers: () => ({
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      }),
      // Transform messages from AI SDK v6 format to backend schema
      prepareSendMessagesRequest: ({ id, messages }) => {
        const transformedMessages = adaptMessagesForBackend(messages);
        const body = {
          id,
          messages: transformedMessages,
          // Send client timezone info for time-aware AI responses
          clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          clientOffset: new Date().getTimezoneOffset(),
        };
        return { body };
      },
      // Reconcile the backend's newer AI SDK stream with our pinned client.
      fetch: async (url: string | URL | Request, options?: RequestInit) =>
        sanitizeStreamResponse(await fetch(url, options)),
    }),
    // V6: Auto-send approval responses to continue conversation (SEQUENTIAL VERSION)
    // This tells the SDK to automatically send the message after each individual approval response
    // For sequential flow, we don't wait for ALL approvals - we process one at a time
    sendAutomaticallyWhen: ({ messages: allMessages }) =>
      shouldAutoSendAfterApprovals(allMessages as ChatMessage[], null),
    // Enhanced error handler - parse validation errors into user-friendly messages
    onError: (error) => {
      // Check if this is a type validation error
      const isTypeValidationError = error.name === 'AI_TypeValidationError' ||
                                      error.message?.includes('Type validation failed');

      if (isTypeValidationError) {
        // No validation error is suppressed here any more. The two that used to
        // be — reasoning chunks and `finish`/`finishReason` — were version-drift
        // artifacts that streamChunkSanitizer now fixes at the source. Matching
        // them by substring was also unsafe: a Zod error message embeds the
        // offending chunk, so any unrelated failure whose payload happened to
        // contain "reasoning" was silently discarded and the user lost the rest
        // of the stream with no error at all.

        // Log full details for debugging
        console.group('⚠️ [V6] Type Validation Error (User will see friendly message)');
        console.error('Error name:', error.name);
        console.error('Error message (first 500 chars):', error.message?.substring(0, 500));
        console.error('Full error object:', error);
        console.groupEnd();

        // Parse validation error into user-friendly message
        const friendlyMessage = parseTypeValidationError(error);
        const userFriendlyError = new Error(friendlyMessage);
        (userFriendlyError as any).originalError = error;
        (userFriendlyError as any).isTypeValidationError = true;

        throw userFriendlyError;
      }

      // Log other non-validation errors
      console.error('❌ [Chat Error] Full details:', {
        name: error.name,
        message: error.message,
        cause: error.cause,
        stack: error.stack,
        fullError: error,
      });

      // Create generic user-friendly error (bias toward connectivity)
      const userFriendlyError = new Error('Connection issue. Please try again.');
      (userFriendlyError as any).originalError = error;

      throw userFriendlyError;
    },
  });

  return {
    ...chatHook,
    chatId,
    chatEndpoint,
    // Type-safe messages
    messages: chatHook.messages as ChatMessage[],
  };
}
